import * as Icons from '@ant-design/icons';
import { Button, Card, DatePicker, Flex, Form, Input, Typography } from 'antd';
import { FieldsetTitle } from 'components/professor_area/FieldsetTitle';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import useNotification from 'hooks/useNotification';
import { useUpdate, useCreate } from '@refinedev/core';

interface ShippingInfoSectionProps {
  certificate: any;
  form: any;
}

export const ShippingInfoSection = ({ certificate, form }: ShippingInfoSectionProps) => {
  const { showError } = useNotification();
  const [isEditingShippingInfo, setIsEditingShippingInfo] = useState(false);
  const { mutateAsync: updateShipment, isLoading: isUpdatingShipment } = useUpdate();
  const { mutateAsync: createShipment, isLoading: isCreatingShipment } = useCreate();

  // Função para desabilitar datas futuras
  const disabledDate = (current: dayjs.Dayjs) => current && current.isAfter(dayjs(), 'day');

  const handleShippingInfoAction = useCallback(async () => {
    if (!isEditingShippingInfo) {
      setIsEditingShippingInfo(true);
    } else {
      try {
        const values = await form.validateFields(['tracking_code', 'posted_at']);
        
        if (!values.tracking_code || !values.posted_at) {
          showError('Código de rastreio e data de envio são obrigatórios');
          return;
        }
        
        const shipmentExists = certificate?.last_shipment?.id;
        const formattedDate = values.posted_at.format('YYYY-MM-DD');

        if (shipmentExists) {
          // UPDATE - Atualizar shipment existente
          await updateShipment({
            resource: 'certificate_shipments',
            id: certificate.last_shipment.id,
            values: {
              tracking_code: values.tracking_code,
              posted_at: formattedDate
            },
            successNotification: () => ({
              type: 'success',
              description: 'Sucesso!',
              message: 'Informações de envio salvas com sucesso!'
            }),
            errorNotification: () => ({
              type: 'error',
              description: 'Erro!',
              message: 'Erro ao salvar informações de envio. Tente novamente.'
            })
          });
        } else {
          // CREATE - Criar novo shipment
          if (!certificate?.id) {
            showError('ID do certificado não encontrado');
            return;
          }

          await createShipment({
            resource: 'certificate_shipments',
            values: {
              graduate_course_certificate_id: certificate.id,
              tracking_code: values.tracking_code,
              posted_at: formattedDate
            },
            successNotification: () => ({
              type: 'success',
              description: 'Sucesso!',
              message: 'Informações de envio salvas com sucesso!'
            }),
            errorNotification: () => ({
              type: 'error',
              description: 'Erro!',
              message: 'Erro ao salvar informações de envio. Tente novamente.'
            })
          });
        }

        setIsEditingShippingInfo(false);
      } catch (error) {
        console.error('Erro ao salvar informações de envio:', error);
      }
    }
  }, [isEditingShippingInfo, form, certificate?.last_shipment?.id, certificate?.id, updateShipment, createShipment, showError]);

  return (
    <fieldset className="mb-6">
      <FieldsetTitle
        title="Informações de envio"
        icon={Icons.SendOutlined}
        hint={`Acompanhamento pelos Correios do envio do certificado nº ${certificate?.registration_number || ''}`}
      />

      <Card size="small" className="mt-6">
        <Flex vertical gap={20}>
          <Flex gap={16} wrap>
            <div className="flex-1 min-w-60">
              <div>
                <Typography.Title level={5} className="!mb-2">
                  Código de rastreio
                </Typography.Title>
                <Typography.Text type="secondary" className="text-sm">
                  Registro do código de rastreio do certificado enviado para o aluno
                </Typography.Text>
              </div>
              <Form.Item 
                name="tracking_code" 
                className="!mb-0 flex-1 mt-4"
                rules={[{ required: true, message: 'Código de rastreio é obrigatório' }]}
              >
                <Input 
                  placeholder="Digite o código de rastreio"
                  className="w-full"
                  disabled={!isEditingShippingInfo}
                />
              </Form.Item>
            </div>

            <div className="flex-1 min-w-60">
              <div>
                <Typography.Title level={5} className="!mb-2">
                  Enviado para o aluno
                </Typography.Title>
                <Typography.Text type="secondary" className="text-sm">
                  Registro da data em que o certificado foi enviado para o aluno
                </Typography.Text>
              </div>

              <Form.Item 
                name="posted_at" 
                className="!mb-0 mt-4"
                rules={[{ required: true, message: 'Data de envio é obrigatória' }]}
              >
                <DatePicker 
                  placeholder="Selecione a data do envio"
                  format="DD/MM/YYYY"
                  className="w-full"
                  disabled={!isEditingShippingInfo}
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </div>
          </Flex>

          <Flex justify="center">
            <Button
              type="primary"
              icon={isEditingShippingInfo ? <Icons.SaveOutlined /> : (certificate?.last_shipment?.tracking_code || certificate?.last_shipment?.posted_at ? <Icons.EditOutlined /> : <Icons.PlusOutlined />)}
              loading={(isUpdatingShipment || isCreatingShipment) && isEditingShippingInfo}
              disabled={isUpdatingShipment || isCreatingShipment}
              onClick={handleShippingInfoAction}
            >
              {isEditingShippingInfo ? 'Salvar informações de envio' : (certificate?.last_shipment?.tracking_code || certificate?.last_shipment?.posted_at ? 'Editar informações' : 'Adicionar informações')}
            </Button>
          </Flex>
        </Flex>
      </Card>
    </fieldset>
  );
};
